import { supabase } from './supabase';

export interface AccountCompletionStatus {
  isComplete: boolean;
  hasFullName: boolean;
  hasEmail: boolean;
  emailVerified: boolean;
  markedComplete: boolean;
  missingFields: string[];
}

export const accountValidationService = {
  async checkAccountCompletion(userId: string): Promise<AccountCompletionStatus> {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('full_name, email, email_verified, account_complete')
        .eq('id', userId)
        .maybeSingle();

      if (error || !profile) {
        return {
          isComplete: false,
          hasFullName: false,
          hasEmail: false,
          emailVerified: false,
          markedComplete: false,
          missingFields: ['profile'],
        };
      }

      const hasFullName = !!(profile.full_name && profile.full_name.trim() !== '');
      const hasEmail = !!(profile.email && profile.email.trim() !== '');
      const emailVerified = profile.email_verified === true;
      const markedComplete = profile.account_complete === true;

      const missingFields: string[] = [];
      if (!hasFullName) missingFields.push('full_name');
      if (!hasEmail) missingFields.push('email');
      if (!emailVerified) missingFields.push('email_verification');
      if (!markedComplete) missingFields.push('account_setup');

      const isComplete = hasFullName && hasEmail && emailVerified && markedComplete;

      return {
        isComplete,
        hasFullName,
        hasEmail,
        emailVerified,
        markedComplete,
        missingFields,
      };
    } catch (error) {
      console.error('Error checking account completion:', error);
      return {
        isComplete: false,
        hasFullName: false,
        hasEmail: false,
        emailVerified: false,
        markedComplete: false,
        missingFields: ['error'],
      };
    }
  },

  async markAccountComplete(userId: string): Promise<boolean> {
    try {
      const status = await this.checkAccountCompletion(userId);

      if (!status.hasFullName || !status.hasEmail || !status.emailVerified) {
        console.warn('Cannot mark account complete: missing required fields');
        return false;
      }

      const { error } = await supabase
        .from('profiles')
        .update({ account_complete: true })
        .eq('id', userId);

      if (error) {
        console.error('Error marking account complete:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in markAccountComplete:', error);
      return false;
    }
  },

  async markEmailVerified(userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ email_verified: true })
        .eq('id', userId);

      if (error) {
        console.error('Error marking email verified:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in markEmailVerified:', error);
      return false;
    }
  },

  async validateReferralAccounts(referralId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('validate_referral_accounts', {
        referral_id: referralId,
      });

      if (error) {
        console.error('Error validating referral accounts:', error);
        return false;
      }

      return data === true;
    } catch (error) {
      console.error('Error in validateReferralAccounts:', error);
      return false;
    }
  },

  async updatePhoneNumber(userId: string, phoneNumber: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ phone_number: phoneNumber })
        .eq('id', userId);

      if (error) {
        console.error('Error updating phone number:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updatePhoneNumber:', error);
      return false;
    }
  },

  getCompletionMessage(status: AccountCompletionStatus): string {
    if (status.isComplete) {
      return 'Your account is complete!';
    }

    const missing = status.missingFields;
    if (missing.includes('full_name')) {
      return 'Please complete your profile by adding your full name.';
    }
    if (missing.includes('email_verification')) {
      return 'Please verify your email address to complete your account.';
    }
    if (missing.includes('account_setup')) {
      return 'Please complete your profile setup.';
    }

    return 'Please complete your account to unlock all features.';
  },
};
